import React, { createContext, useContext, useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from './ThemeContext';

const ModalDialogContext = createContext(null);

let globalTrigger = null;

export const setGlobalModalTrigger = (trigger) => {
  globalTrigger = trigger;
};

// Global standalone toast notification function
export const showToast = (message, type = 'success', durationMs = 4000) => {
  if (globalTrigger?.toast) {
    return globalTrigger.toast(message, type, durationMs);
  }
  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
    window.dispatchEvent(new CustomEvent('app:toast', { detail: { message, type, durationMs } }));
  }
};

// Global standalone functions accessible outside React hooks
export const modal = {
  alert: (message, options) => {
    if (globalTrigger?.alert) {
      return globalTrigger.alert(message, options);
    }
    window.alert(typeof message === 'string' ? message : JSON.stringify(message));
    return Promise.resolve();
  },
  confirm: (message, options) => {
    if (globalTrigger?.confirm) {
      return globalTrigger.confirm(message, options);
    }
    // Fallback if provider not mounted yet
    const msgText = typeof message === 'string' ? message : (message?.message || '');
    return Promise.resolve(window.confirm(msgText));
  },
  prompt: (message, defaultValue = '', options) => {
    if (globalTrigger?.prompt) {
      return globalTrigger.prompt(message, defaultValue, options);
    }
    const msgText = typeof message === 'string' ? message : (message?.message || '');
    return Promise.resolve(window.prompt(msgText, defaultValue));
  },
  toast: (message, type = 'success', durationMs = 4000) => showToast(message, type, durationMs),
  notify: (message, type = 'success', durationMs = 4000) => showToast(message, type, durationMs),
  success: (message, durationMs = 4000) => showToast(message, 'success', durationMs),
  error: (message, durationMs = 5000) => showToast(message, 'error', durationMs),
  info: (message, durationMs = 4000) => showToast(message, 'info', durationMs),
};

export function useModal() {
  const context = useContext(ModalDialogContext);
  if (!context) {
    return modal;
  }
  return context;
}

export function ModalDialogProvider({ children }) {
  const { tenantConfig } = useTheme() || {};
  const [modalState, setModalState] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [inputError, setInputError] = useState('');
  const inputRef = useRef(null);
  const resolverRef = useRef(null);

  // Slide-in top banner toast notifications
  const [toasts, setToasts] = useState([]);
  const recentToastsRef = useRef(new Map());

  const dismissToast = useCallback((id) => {
    setToasts((prev) =>
      prev.map((t) => (t.id === id ? { ...t, isLeaving: true } : t))
    );
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 320);
  }, []);

  const showToastNotification = useCallback((message, type = 'success', durationMs = 4000) => {
    if (!message) return;
    const msgText = typeof message === 'string' ? message : (message?.message || message?.detail || JSON.stringify(message));

    // Suppress rapid repeated calls with identical message & type within 2.5 seconds
    const now = Date.now();
    const key = `${type || 'success'}:${msgText}`;
    const lastShown = recentToastsRef.current.get(key) || 0;
    if (now - lastShown < 2500) {
      return;
    }
    recentToastsRef.current.set(key, now);
    if (recentToastsRef.current.size > 50) {
      for (const [k, time] of recentToastsRef.current.entries()) {
        if (now - time > 10000) recentToastsRef.current.delete(k);
      }
    }

    const id = `toast_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
    const effectiveDuration = durationMs || (type === 'error' || type === 'danger' ? 5000 : 4000);

    const newToast = {
      id,
      message: msgText,
      type: type || 'success',
      durationMs: effectiveDuration,
      createdAt: Date.now(),
      isLeaving: false
    };

    setToasts((prev) => {
      const active = prev.filter((t) => !t.isLeaving);
      // Avoid duplicate toasts with the identical message & type
      if (active.some((t) => t.message === msgText && t.type === newToast.type)) {
        return prev;
      }
      const trimmed = active.length >= 3 ? active.slice(active.length - 2) : active;
      return [...trimmed, newToast];
    });

    const timer = setTimeout(() => {
      dismissToast(id);
    }, effectiveDuration);

    return () => clearTimeout(timer);
  }, [dismissToast]);

  // Listen for custom app:toast events on window
  useEffect(() => {
    const handleToastEvent = (e) => {
      if (e?.detail) {
        showToastNotification(e.detail.message, e.detail.type, e.detail.durationMs);
      }
    };
    window.addEventListener('app:toast', handleToastEvent);
    return () => window.removeEventListener('app:toast', handleToastEvent);
  }, [showToastNotification]);

  const brandName = tenantConfig?.brand_name || 'TrophyCoin';

  // 1. showAlert
  const showAlert = useCallback((message, options = {}) => {
    return new Promise((resolve) => {
      resolverRef.current = () => {
        setModalState(null);
        resolve();
      };
      const title = options.title || 'Notice';
      const type = options.type || 'info';
      const buttonText = options.buttonText || options.confirmText || 'OK';

      setModalState({
        kind: 'alert',
        title,
        message,
        type,
        buttonText
      });
    });
  }, []);

  // 2. showConfirm
  const showConfirm = useCallback((message, options = {}) => {
    return new Promise((resolve) => {
      resolverRef.current = (result) => {
        setModalState(null);
        resolve(!!result);
      };
      const title = options.title || 'Please Confirm';
      const danger = options.danger !== undefined ? options.danger : (options.type === 'danger' || /delete|remove|revoke|archive|irreversible/i.test(String(message)));
      const type = danger ? 'danger' : (options.type || 'confirm');
      const confirmText = options.confirmText || (danger ? 'Delete' : 'Confirm');
      const cancelText = options.cancelText || 'Cancel';

      setModalState({
        kind: 'confirm',
        title,
        message,
        type,
        danger,
        confirmText,
        cancelText
      });
    });
  }, []);

  // 3. showPrompt
  const showPrompt = useCallback((message, defaultValue = '', options = {}) => {
    return new Promise((resolve) => {
      resolverRef.current = (result) => {
        setModalState(null);
        resolve(result);
      };
      const title = options.title || 'Input Required';
      const placeholder = options.placeholder || '';
      const confirmText = options.confirmText || 'Save';
      const cancelText = options.cancelText || 'Cancel';
      const validate = options.validate || null;

      setInputValue(defaultValue);
      setInputError('');
      setModalState({
        kind: 'prompt',
        title,
        message,
        type: 'prompt',
        placeholder,
        confirmText,
        cancelText,
        validate
      });
    });
  }, []);

  // Expose trigger globally and hook up window.alert override
  useEffect(() => {
    const trigger = {
      alert: showAlert,
      confirm: showConfirm,
      prompt: showPrompt,
      toast: showToastNotification,
      notify: showToastNotification,
      success: (msg, d) => showToastNotification(msg, 'success', d),
      error: (msg, d) => showToastNotification(msg, 'error', d),
      info: (msg, d) => showToastNotification(msg, 'info', d),
      dismissToast
    };
    setGlobalModalTrigger(trigger);

    // Global window convenience references
    window.$modal = trigger;
    window.$toast = showToastNotification;
    window.$notify = showToastNotification;
    window.$alert = showAlert;
    window.$confirm = showConfirm;
    window.$prompt = showPrompt;

    // Route legacy window.alert calls to slick HTML alert
    const originalAlert = window.alert;
    window.alert = (msg) => {
      try {
        showAlert(msg);
      } catch (_) {
        originalAlert(msg);
      }
    };

    return () => {
      setGlobalModalTrigger(null);
      window.alert = originalAlert;
    };
  }, [showAlert, showConfirm, showPrompt, showToastNotification, dismissToast]);

  // Focus prompt input when shown
  useEffect(() => {
    if (modalState?.kind === 'prompt' && inputRef.current) {
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          inputRef.current.select();
        }
      }, 50);
    }
  }, [modalState?.kind]);

  // Keyboard navigation (Enter / Escape)
  useEffect(() => {
    if (!modalState) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        if (modalState.kind === 'prompt') {
          if (resolverRef.current) resolverRef.current(null);
        } else if (modalState.kind === 'confirm') {
          if (resolverRef.current) resolverRef.current(false);
        } else {
          if (resolverRef.current) resolverRef.current();
        }
      } else if (e.key === 'Enter') {
        if (modalState.kind === 'prompt') {
          e.preventDefault();
          e.stopPropagation();
          handlePromptSubmit();
        } else if (modalState.kind === 'confirm') {
          e.preventDefault();
          e.stopPropagation();
          if (resolverRef.current) resolverRef.current(true);
        } else if (modalState.kind === 'alert') {
          e.preventDefault();
          e.stopPropagation();
          if (resolverRef.current) resolverRef.current();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalState, inputValue]);

  const handlePromptSubmit = () => {
    if (modalState?.validate) {
      const err = modalState.validate(inputValue);
      if (err) {
        setInputError(err);
        return;
      }
    }
    if (resolverRef.current) resolverRef.current(inputValue);
  };

  const handlePromptCancel = () => {
    if (resolverRef.current) resolverRef.current(null);
  };

  const handleConfirmAction = (confirmed) => {
    if (resolverRef.current) resolverRef.current(confirmed);
  };

  const handleAlertClose = () => {
    if (resolverRef.current) resolverRef.current();
  };

  const renderIcon = () => {
    if (!modalState) return null;
    const { type, danger } = modalState;

    if (danger || type === 'danger') {
      return (
        <div style={{
          width: '42px',
          height: '42px',
          borderRadius: '12px',
          backgroundColor: 'rgba(239, 68, 68, 0.15)',
          border: '1px solid rgba(239, 68, 68, 0.35)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '20px',
          flexShrink: 0
        }}>
          ⚠️
        </div>
      );
    }

    if (type === 'success') {
      return (
        <div style={{
          width: '42px',
          height: '42px',
          borderRadius: '12px',
          backgroundColor: 'rgba(34, 197, 94, 0.15)',
          border: '1px solid rgba(34, 197, 94, 0.35)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '20px',
          color: '#4ade80',
          flexShrink: 0
        }}>
          ✓
        </div>
      );
    }

    if (modalState.kind === 'prompt') {
      return (
        <div style={{
          width: '42px',
          height: '42px',
          borderRadius: '12px',
          backgroundColor: 'rgba(56, 189, 248, 0.15)',
          border: '1px solid rgba(56, 189, 248, 0.35)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '20px',
          color: 'var(--primary-color, #38bdf8)',
          flexShrink: 0
        }}>
          ✏️
        </div>
      );
    }

    return (
      <div style={{
        width: '42px',
        height: '42px',
        borderRadius: '12px',
        backgroundColor: 'rgba(56, 189, 248, 0.15)',
        border: '1px solid rgba(56, 189, 248, 0.35)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '20px',
        color: 'var(--primary-color, #38bdf8)',
        flexShrink: 0
      }}>
        ℹ️
      </div>
    );
  };

  const success = useCallback((msg, d) => showToastNotification(msg, 'success', d), [showToastNotification]);
  const error = useCallback((msg, d) => showToastNotification(msg, 'error', d), [showToastNotification]);
  const info = useCallback((msg, d) => showToastNotification(msg, 'info', d), [showToastNotification]);

  const contextValue = useMemo(() => ({
    alert: showAlert,
    confirm: showConfirm,
    prompt: showPrompt,
    toast: showToastNotification,
    notify: showToastNotification,
    success,
    error,
    info,
    dismissToast,
    showToast: showToastNotification
  }), [showAlert, showConfirm, showPrompt, showToastNotification, success, error, info, dismissToast]);

  return (
    <ModalDialogContext.Provider value={contextValue}>
      {children}
      {modalState && typeof document !== 'undefined' && createPortal(
        <div
          data-testid="slick-modal-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.72)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 99999999,
            padding: '16px',
            animation: 'slickFadeIn 0.18s ease-out'
          }}
          onClick={(e) => {
            // Dismiss on background click
            if (e.target === e.currentTarget) {
              if (modalState.kind === 'prompt') handlePromptCancel();
              else if (modalState.kind === 'confirm') handleConfirmAction(false);
              else handleAlertClose();
            }
          }}
        >
          <style>{`
            @keyframes slickFadeIn {
              from { opacity: 0; }
              to { opacity: 1; }
            }
            @keyframes slickModalPop {
              from { opacity: 0; transform: scale(0.93) translateY(10px); }
              to { opacity: 1; transform: scale(1) translateY(0); }
            }
          `}</style>

          <div
            data-testid="slick-modal-container"
            style={{
              background: 'var(--card-bg, #161b22)',
              border: `1px solid var(--border-color, #30363d)`,
              borderTop: modalState.danger
                ? '3px solid #ef4444'
                : '3px solid var(--primary-color, #38bdf8)',
              borderRadius: '16px',
              width: '100%',
              maxWidth: '460px',
              boxShadow: '0 25px 65px rgba(0, 0, 0, 0.65), 0 0 1px 1px rgba(255, 255, 255, 0.05)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              animation: 'slickModalPop 0.22s cubic-bezier(0.16, 1, 0.3, 1)',
              color: 'var(--text-main, #f8fafc)',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
            }}
          >
            {/* Header */}
            <div style={{
              padding: '18px 20px',
              borderBottom: '1px solid var(--border-color, #30363d)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: 'var(--panel-bg, #0d1117)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {renderIcon()}
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
                    <span style={{
                      fontSize: '10px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                      fontWeight: 700,
                      color: modalState.danger ? '#f87171' : 'var(--primary-color, #38bdf8)'
                    }}>
                      {brandName}
                    </span>
                  </div>
                  <h3
                    data-testid="slick-modal-title"
                    style={{
                      margin: 0,
                      fontSize: '15px',
                      fontWeight: 700,
                      color: 'var(--text-main, #ffffff)'
                    }}
                  >
                    {modalState.title}
                  </h3>
                </div>
              </div>

              <button
                type="button"
                data-testid="slick-modal-close-btn"
                onClick={() => {
                  if (modalState.kind === 'prompt') handlePromptCancel();
                  else if (modalState.kind === 'confirm') handleConfirmAction(false);
                  else handleAlertClose();
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--text-mute, #8b949e)',
                  fontSize: '18px',
                  lineHeight: '1',
                  cursor: 'pointer',
                  padding: '6px',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title="Close"
              >
                ✕
              </button>
            </div>

            {/* Body */}
            <div style={{
              padding: '20px',
              display: 'flex',
              flexDirection: 'column',
              gap: '14px',
              background: 'var(--card-bg, #161b22)'
            }}>
              <div
                data-testid="slick-modal-message"
                style={{
                  fontSize: '13.5px',
                  lineHeight: '1.55',
                  color: 'var(--text-main, #e2e8f0)',
                  whiteSpace: 'pre-line',
                  wordBreak: 'break-word'
                }}
              >
                {modalState.message}
              </div>

              {/* Prompt Input */}
              {modalState.kind === 'prompt' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '4px' }}>
                  <input
                    ref={inputRef}
                    data-testid="slick-modal-input"
                    type="text"
                    value={inputValue}
                    placeholder={modalState.placeholder}
                    onChange={(e) => {
                      setInputValue(e.target.value);
                      if (inputError) setInputError('');
                    }}
                    style={{
                      width: '100%',
                      boxSizing: 'border-box',
                      background: 'var(--input-bg, #0d1117)',
                      color: 'var(--input-text, #ffffff)',
                      border: inputError
                        ? '1.5px solid #ef4444'
                        : '1.5px solid var(--primary-color, #38bdf8)',
                      borderRadius: '8px',
                      padding: '10px 14px',
                      fontSize: '14px',
                      outline: 'none',
                      boxShadow: inputError
                        ? '0 0 0 3px rgba(239, 68, 68, 0.15)'
                        : '0 0 0 3px rgba(56, 189, 248, 0.15)',
                      transition: 'border-color 0.15s, box-shadow 0.15s'
                    }}
                  />
                  {inputError && (
                    <span style={{ fontSize: '11px', color: '#f87171' }}>
                      ⚠️ {inputError}
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Footer Buttons */}
            <div style={{
              padding: '14px 20px',
              borderTop: '1px solid var(--border-color, #30363d)',
              background: 'var(--panel-bg, #0d1117)',
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: '10px'
            }}>
              {/* Cancel Button for Confirm / Prompt */}
              {(modalState.kind === 'confirm' || modalState.kind === 'prompt') && (
                <button
                  type="button"
                  data-testid="slick-modal-cancel-btn"
                  onClick={() => {
                    if (modalState.kind === 'prompt') handlePromptCancel();
                    else handleConfirmAction(false);
                  }}
                  style={{
                    padding: '8px 16px',
                    fontSize: '13px',
                    fontWeight: 600,
                    borderRadius: '8px',
                    backgroundColor: 'transparent',
                    color: 'var(--text-mute, #94a3b8)',
                    border: '1px solid var(--border-color, #30363d)',
                    cursor: 'pointer',
                    transition: 'all 0.15s'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.color = 'var(--text-main, #ffffff)';
                    e.currentTarget.style.borderColor = 'var(--text-mute, #64748b)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.color = 'var(--text-mute, #94a3b8)';
                    e.currentTarget.style.borderColor = 'var(--border-color, #30363d)';
                  }}
                >
                  {modalState.cancelText || 'Cancel'}
                </button>
              )}

              {/* Confirm / Save / OK Action Button */}
              <button
                type="button"
                data-testid="slick-modal-confirm-btn"
                autoFocus={modalState.kind !== 'prompt'}
                onClick={() => {
                  if (modalState.kind === 'prompt') handlePromptSubmit();
                  else if (modalState.kind === 'confirm') handleConfirmAction(true);
                  else handleAlertClose();
                }}
                style={{
                  padding: '8px 18px',
                  fontSize: '13px',
                  fontWeight: 700,
                  borderRadius: '8px',
                  backgroundColor: modalState.danger
                    ? '#ef4444'
                    : 'var(--primary-gradient, var(--primary-color, #38bdf8))',
                  color: '#ffffff',
                  border: 'none',
                  cursor: 'pointer',
                  boxShadow: modalState.danger
                    ? '0 4px 14px rgba(239, 68, 68, 0.4)'
                    : '0 4px 14px rgba(0, 0, 0, 0.3)',
                  transition: 'transform 0.1s, filter 0.15s'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.filter = 'brightness(1.08)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.filter = 'brightness(1)';
                }}
              >
                {modalState.kind === 'alert'
                  ? (modalState.buttonText || 'OK')
                  : (modalState.confirmText || 'Confirm')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Slide-in Top Banner Toast Container (Visible over sticky header regardless of scroll position) */}
      {toasts.length > 0 && typeof document !== 'undefined' && document.body && createPortal(
        <div
          data-testid="top-banner-toast-container"
          style={{
            position: 'fixed',
            top: '16px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 100000000,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '10px',
            width: '100%',
            maxWidth: '560px',
            padding: '0 16px',
            boxSizing: 'border-box',
            pointerEvents: 'none',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
          }}
        >
          <style>{`
            @keyframes topToastSlideIn {
              from {
                opacity: 0;
                transform: translateY(-30px) scale(0.96);
              }
              to {
                opacity: 1;
                transform: translateY(0) scale(1);
              }
            }
            @keyframes topToastSlideOut {
              from {
                opacity: 1;
                transform: translateY(0) scale(1);
                max-height: 140px;
              }
              to {
                opacity: 0;
                transform: translateY(-24px) scale(0.96);
                max-height: 0;
                margin-bottom: -10px;
                padding-top: 0;
                padding-bottom: 0;
              }
            }
            @keyframes toastProgressCountdown {
              from { width: 100%; }
              to { width: 0%; }
            }
          `}</style>
          {toasts.map((toast) => {
            const isError = toast.type === 'error' || toast.type === 'danger';
            const isWarning = toast.type === 'warning';
            const isInfo = toast.type === 'info';
            const accentColor = isError
              ? '#ef4444'
              : isWarning
              ? '#f59e0b'
              : isInfo
              ? 'var(--primary-color, #38bdf8)'
              : '#10b981';
            const badgeBg = isError
              ? 'rgba(239, 68, 68, 0.18)'
              : isWarning
              ? 'rgba(245, 158, 11, 0.18)'
              : isInfo
              ? 'rgba(56, 189, 248, 0.18)'
              : 'rgba(16, 185, 129, 0.18)';
            const badgeBorder = isError
              ? 'rgba(239, 68, 68, 0.4)'
              : isWarning
              ? 'rgba(245, 158, 11, 0.4)'
              : isInfo
              ? 'rgba(56, 189, 248, 0.4)'
              : 'rgba(16, 185, 129, 0.4)';
            const iconChar = isError ? '⚠️' : isWarning ? '⚠️' : isInfo ? 'ℹ️' : '✓';
            const typeLabel = isError ? 'Error' : isWarning ? 'Warning' : isInfo ? 'Notice' : 'Success';

            return (
              <div
                key={toast.id}
                data-testid={`top-banner-toast-${toast.type}`}
                role="alert"
                style={{
                  pointerEvents: 'auto',
                  width: '100%',
                  boxSizing: 'border-box',
                  position: 'relative',
                  overflow: 'hidden',
                  borderRadius: '14px',
                  background: 'var(--toast-bg, rgba(15, 23, 42, 0.94))',
                  backdropFilter: 'blur(16px) saturate(180%)',
                  WebkitBackdropFilter: 'blur(16px) saturate(180%)',
                  border: '1px solid var(--toast-border, rgba(255, 255, 255, 0.14))',
                  borderTop: `3px solid ${accentColor}`,
                  boxShadow: '0 20px 45px -10px rgba(0, 0, 0, 0.7), 0 0 1px 1px rgba(255, 255, 255, 0.08)',
                  animation: toast.isLeaving
                    ? 'topToastSlideOut 0.32s cubic-bezier(0.4, 0, 0.2, 1) forwards'
                    : 'topToastSlideIn 0.35s cubic-bezier(0.16, 1, 0.3, 1) forwards',
                  color: 'var(--toast-text, #ffffff)',
                  display: 'flex',
                  flexDirection: 'column',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
                }}
              >
                <div style={{
                  padding: '12px 16px',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px',
                  width: '100%',
                  boxSizing: 'border-box'
                }}>
                  {/* Icon badge */}
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '10px',
                    backgroundColor: badgeBg,
                    border: `1px solid ${badgeBorder}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '15px',
                    color: accentColor,
                    flexShrink: 0,
                    marginTop: '1px'
                  }}>
                    {iconChar}
                  </div>

                  {/* Body */}
                  <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '3px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{
                        fontSize: '9.5px',
                        fontWeight: 800,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        color: accentColor
                      }}>
                        {brandName} • {typeLabel}
                      </span>
                    </div>
                    <div style={{
                      fontSize: '13.5px',
                      lineHeight: '1.45',
                      color: 'var(--toast-text, #f8fafc)',
                      fontWeight: 500,
                      wordBreak: 'break-word',
                      whiteSpace: 'pre-line'
                    }}>
                      {toast.message}
                    </div>
                  </div>

                  {/* Close button */}
                  <button
                    type="button"
                    data-testid="top-banner-toast-dismiss-btn"
                    onClick={() => dismissToast(toast.id)}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: 'var(--toast-text, #94a3b8)',
                      opacity: 0.75,
                      fontSize: '16px',
                      cursor: 'pointer',
                      padding: '4px',
                      lineHeight: '1',
                      borderRadius: '6px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      marginTop: '-2px',
                      marginRight: '-4px',
                      transition: 'color 0.15s, opacity 0.15s, background-color 0.15s'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.opacity = '1';
                      e.currentTarget.style.backgroundColor = 'rgba(128, 128, 128, 0.15)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.opacity = '0.75';
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                    aria-label="Dismiss message"
                  >
                    ✕
                  </button>
                </div>

                {/* Progress countdown bar */}
                <div style={{
                  width: '100%',
                  height: '2.5px',
                  backgroundColor: 'rgba(255, 255, 255, 0.08)',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    height: '100%',
                    backgroundColor: accentColor,
                    animation: `toastProgressCountdown ${toast.durationMs}ms linear forwards`
                  }} />
                </div>
              </div>
            );
          })}
        </div>,
        document.body
      )}
    </ModalDialogContext.Provider>
  );
}
